// =====================================
// useNewRefillsLogic.jsx - VERSIÓN CORREGIDA Y COMPATIBLE
// =====================================

import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSalesContext } from "../contexts/SalesContext";
import { useSupaContext } from "../contexts/SupaContext";
import toast from "react-hot-toast";

// 1. Definimos los pasos del flujo de recargas
export const STEP_CONFIG = {
  VALIDATION: "validation",
  SCHEDULE: "schedule",
  SUMMARY: "summary",
};

export const useNewRefillsLogic = ({ refillData, jugsData } = {}) => {
  const navigate = useNavigate();

  // 2. Obtenemos datos de los contextos
  const {
    refillItems,
    refillJugsData,
    client,
    deliveryDate,
    setDeliveryDate,
    setTotal,
    startRefillProcess,
    triggerNewShopLeadGeneration,
    selectedDeliveryDate,
    setSelectedDeliveryDate,
    newSalesTerms,
    setNewSalesTerms,
  } = useSalesContext();

  const { isActiveUser, user } = useSupaContext();

  // 3. Estado para gestionar el flujo y la UI
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);

  const availableSteps = useMemo(
    () => [STEP_CONFIG.VALIDATION, STEP_CONFIG.SCHEDULE, STEP_CONFIG.SUMMARY],
    []
  );

  const currentStep = availableSteps[currentStepIndex];

  // 4. SINCRONIZACIÓN DE DATOS - CORREGIDO para evitar setState en render
  useEffect(() => {
    if (!hasInitialized && refillData && jugsData) {
      console.log("🔄 useNewRefillsLogic: Inicializando con datos de props", {
        itemsCount: refillData.items?.length || 0,
        jugsCount: jugsData?.length || 0,
      });

      if (startRefillProcess && refillData.items) {
        // Usar setTimeout para evitar setState durante render
        setTimeout(() => {
          startRefillProcess(refillData.items, jugsData);
        }, 0);
      }

      setHasInitialized(true);
    }
  }, [refillData, jugsData, startRefillProcess, hasInitialized]);

  // 5. Determinamos qué datos usar
  const activeRefillItems = useMemo(() => {
    if (refillItems && refillItems.length > 0) {
      return refillItems;
    }
    if (refillData?.items && refillData.items.length > 0) {
      return refillData.items;
    }
    return [];
  }, [refillItems, refillData]);

  const activeJugsData = useMemo(() => {
    if (refillJugsData && refillJugsData.length > 0) {
      return refillJugsData;
    }
    if (jugsData && jugsData.length > 0) {
      return jugsData;
    }
    return [];
  }, [refillJugsData, jugsData]);

  // 6. GUARDIÁN: Redirige si no hay datos
  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeRefillItems.length === 0) {
        console.warn(
          "useNewRefillsLogic: No hay datos de recarga. Redirigiendo..."
        );
        navigate("/app", {
          replace: true,
          state: {
            error: "Por favor, selecciona primero los botellones a recargar.",
          },
        });
      } else {
        setIsLoading(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [activeRefillItems, navigate]);

  // 7. ✅ AGRUPACIÓN COMPATIBLE - Mantiene la estructura esperada por los componentes
  const groupedByAddress = useMemo(() => {
    if (!activeRefillItems || activeRefillItems.length === 0) return {};

    console.log("🔄 Agrupando items por dirección:", activeRefillItems);

    // ✅ MANTENER estructura original que esperan los componentes
    const grouped = activeRefillItems.reduce((acc, item) => {
      const { address } = item;
      if (!acc[address]) acc[address] = [];

      // ✅ ASEGURAR que el item tenga todas las propiedades necesarias
      const itemWithDefaults = {
        jugId: item.jugId,
        barcode: item.barcode,
        addressId: item.addressId,
        address: item.address,
        city: item.city,
        price: item.price || 15000, // Precio por defecto
        quantity: item.quantity || 1,
        latitude: item.latitude,
        longitude: item.longitude,
        user_id: item.user_id,
        address_image_url: item.address_image_url || null, // ✅ CORREGIR: Asegurar que existe
        created_at: item.created_at,
        detail_type: item.detail_type,
        order_detail_status: item.order_detail_status,
      };

      acc[address].push(itemWithDefaults);
      return acc;
    }, {});

    console.log("✅ Items agrupados por dirección:", grouped);
    return grouped;
  }, [activeRefillItems]);

  // 8. ✅ CÁLCULO DE TOTALES CORREGIDO - No usar setTotal dentro de useMemo
  const totals = useMemo(() => {
    if (!activeRefillItems || activeRefillItems.length === 0) {
      return {
        totalQuantity: 0,
        totalPrice: 0,
        subtotal: 0,
        deliveryFee: 0,
        total: 0,
      };
    }

    const totalQuantity = activeRefillItems.reduce(
      (sum, item) => sum + (item.quantity || 1),
      0
    );

    const subtotal = activeRefillItems.reduce(
      (sum, item) => sum + (item.price || 15000) * (item.quantity || 1),
      0
    );

    // Calcular delivery fee - por ahora fijo, después será por zonas
    const uniqueAddresses = new Set(
      activeRefillItems.map((item) => item.addressId)
    );
    const deliveryFee = uniqueAddresses.size * 5000; // 5k por dirección

    const total = subtotal + deliveryFee;

    const calculatedTotals = {
      totalQuantity,
      totalPrice: subtotal, // Para compatibilidad
      subtotal,
      deliveryFee,
      total,
      // Versiones formateadas
      subtotalFormatted: new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        minimumFractionDigits: 0,
      }).format(subtotal),
      deliveryFeeFormatted: new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        minimumFractionDigits: 0,
      }).format(deliveryFee),
      totalFormatted: new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        minimumFractionDigits: 0,
      }).format(total),
    };

    console.log("✅ Totales calculados:", calculatedTotals);
    return calculatedTotals;
  }, [activeRefillItems]);

  // 9. ✅ EFECTO SEPARADO para actualizar total en contexto - Evita setState en render
  useEffect(() => {
    if (setTotal && totals.total > 0) {
      setTotal(totals.total);
    }
  }, [totals.total, setTotal]);

  // 10. Lógica de validación por pasos
  const canContinueFromCurrentStep = useMemo(() => {
    switch (currentStep) {
      case STEP_CONFIG.VALIDATION:
        return (
          activeRefillItems.length > 0 &&
          Object.keys(groupedByAddress).length > 0
        );

      case STEP_CONFIG.SCHEDULE:
        return !!selectedDeliveryDate;

      case STEP_CONFIG.SUMMARY:
        // AQUÍ está el problema - agregar validación de términos
        return newSalesTerms && activeRefillItems.length > 0;

      default:
        return false;
    }
  }, [
    currentStep,
    activeRefillItems,
    groupedByAddress,
    selectedDeliveryDate,
    newSalesTerms,
  ]);

  // 11. Mensajes de error específicos
  // Agregar después de canContinueFromCurrentStep
  const getValidationMessage = useCallback(() => {
    if (canContinueFromCurrentStep) return null;

    switch (currentStep) {
      case STEP_CONFIG.VALIDATION:
        if (activeRefillItems.length === 0) return "No hay items para recargar";
        if (Object.keys(groupedByAddress).length === 0)
          return "No hay direcciones válidas";
        return null;

      case STEP_CONFIG.SCHEDULE:
        return "Selecciona una fecha de entrega";

      case STEP_CONFIG.SUMMARY:
        return "Acepta los términos y condiciones para continuar";

      default:
        return null;
    }
  }, [
    currentStep,
    canContinueFromCurrentStep,
    activeRefillItems,
    groupedByAddress,
  ]);

  // 12. ✅ FUNCIÓN PREPARAR DATOS PARA SUPABASE
  const prepareFinalOrderData = useCallback(() => {
    const finalDeliveryDate = selectedDeliveryDate || deliveryDate;

    if (!finalDeliveryDate) {
      throw new Error("Fecha de entrega requerida");
    }

    // Preparar datos en formato que espera createBulkRefillOrder
    const groupedForSupabase = Object.entries(groupedByAddress).map(
      ([address, items]) => ({
        addressId: items[0]?.addressId,
        address: address,
        city: items[0]?.city || "",
        jugs: items.map((item) => ({
          jugId: item.jugId,
          barcode: item.barcode,
          daysWithCustomer: 0, // Calcular si es necesario
        })),
      })
    );

    const orderData = {
      items: activeRefillItems,
      groupedByAddress: groupedForSupabase,
      scheduledDate: finalDeliveryDate,
      timeSlot: "morning", // TODO: Implementar selección
      totals: {
        subtotal: totals.subtotal,
        deliveryFee: totals.deliveryFee,
        total: totals.total,
      },
    };

    console.log("✅ Datos finales preparados para Supabase:", orderData);
    return orderData;
  }, [
    activeRefillItems,
    groupedByAddress,
    totals,
    selectedDeliveryDate,
    deliveryDate,
  ]);

  // 13. ✅ HANDLERS ACTUALIZADOS
  const handleSubmitRefill = useCallback(async () => {
    if (!canContinueFromCurrentStep || isSubmitting) {
      console.log("⚠️ handleSubmitRefill: No se puede procesar", {
        canContinue: canContinueFromCurrentStep,
        isSubmitting,
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Solo disparar el trigger UNA VEZ
      await triggerNewShopLeadGeneration();
      console.log("✅ useNewRefillsLogic: Proceso de refill iniciado");
    } catch (error) {
      console.error("❌ Error al iniciar refill:", error);
      // IMPORTANTE: Resetear el estado si hay error
      setIsSubmitting(false);
      toast.error("Error al procesar tu pedido. Intenta de nuevo.");
    }
    // NO resetear isSubmitting en success
  }, [canContinueFromCurrentStep, triggerNewShopLeadGeneration, isSubmitting]);

  const handleNext = useCallback(() => {
    if (!canContinueFromCurrentStep) {
      const message = getValidationMessage;
      toast.error(message || "Error de validación");
      return;
    }

    if (isSubmitting) {
      console.log("⚠️ handleNext: Ya se está procesando una orden");
      return;
    }

    if (currentStep === STEP_CONFIG.SUMMARY) {
      handleSubmitRefill();
      return;
    }

    if (currentStepIndex < availableSteps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    }
  }, [
    canContinueFromCurrentStep,
    getValidationMessage,
    currentStep,
    currentStepIndex,
    availableSteps,
    handleSubmitRefill,
    isSubmitting,
  ]);

  const handleBack = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    } else {
      navigate("/app");
    }
  }, [currentStepIndex, navigate]);

  // 14. Estado y texto del botón principal
  const isButtonDisabled = isSubmitting || !canContinueFromCurrentStep;

  const getButtonText = useCallback(() => {
    if (isSubmitting) return "Procesando...";
    if (currentStep === STEP_CONFIG.SUMMARY) return "Confirmar e ir a pagar"; // ← Cambiar aquí
    return "Continuar";
  }, [isSubmitting, currentStep]);

  // 15. ✅ OBJETO DE RETORNO COMPATIBLE
  return {
    // Estados del flujo
    currentStep,
    currentStepIndex,
    setCurrentStepIndex,
    availableSteps,
    isLoading,
    isSubmitting,
    newSalesTerms,
    setNewSalesTerms,

    // ✅ Datos procesados - mantener compatibilidad
    refillData: { items: activeRefillItems },
    jugsData: activeJugsData,
    groupedByAddress, // Mantiene estructura original que esperan los componentes
    totals,
    client: client || user,

    // Estado de la UI
    deliveryDate: selectedDeliveryDate || deliveryDate,
    setDeliveryDate: setSelectedDeliveryDate || setDeliveryDate,

    // Navegación
    handleNext,
    handleBack,

    // Estado del botón
    isButtonDisabled,
    getButtonText,

    // Funciones útiles
    prepareFinalOrderData,
    getValidationMessage,

    // Para debugging
    debug: {
      activeRefillItems,
      groupedByAddress,
      totals,
      canContinueFromCurrentStep,
    },
    goToStep: (stepName) => {
      const index = availableSteps.indexOf(stepName);
      if (index !== -1) {
        setCurrentStepIndex(index);
      }
    },
  };
};
